import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, Phone, CheckCircle, Clock, UserCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  isCoordinatorBotConfigured, 
  getCoordinatorBotStats,
  getAllCoordinatorBotConnections,
  disconnectCoordinatorUser,
  type CoordinatorBotConnection
} from '@/services/telegram/coordinatorBotService';

const CoordinatorBotManager: React.FC = () => {
  const { toast } = useToast();
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [stats, setStats] = useState({
    totalConnections: 0,
    verifiedConnections: 0,
    pendingConnections: 0
  });
  const [connections, setConnections] = useState<CoordinatorBotConnection[]>([]);
  const [loading, setLoading] = useState(true);

  const checkConfiguration = async () => {
    try {
      const configured = await isCoordinatorBotConfigured();
      setIsConfigured(configured);
      
      if (configured) {
        const [statsData, connectionsData] = await Promise.all([
          getCoordinatorBotStats(),
          getAllCoordinatorBotConnections(),
        ]);
        
        setStats(statsData);
        setConnections(connectionsData);
      }
    } catch (error) {
      console.error('Error checking coordinator bot configuration:', error);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectUser = async (userId: string) => {
    try {
      const result = await disconnectCoordinatorUser(userId);
      if (result.success) {
        toast({
          title: "User Disconnected",
          description: "The user has been disconnected from the coordinator bot.",
        });
        // Refresh the connections list
        checkConfiguration();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to disconnect user",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to disconnect user",
      });
    }
  };

  useEffect(() => {
    checkConfiguration();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Coordinator Bot Manager
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (isConfigured === false) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Coordinator Bot Manager
          </CardTitle>
          <CardDescription>
            The coordinator bot token is not configured. Please contact your administrator to set up the TELEGRAM_COORDINATOR_BOT_TOKEN.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              The coordinator bot is not properly configured. Please configure the bot token first.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Coordinator Bot Manager
          </CardTitle>
          <CardDescription>
            Manage coordinator access to the Telegram bot for farm management.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              The coordinator bot is properly configured and ready to use.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <h3 className="text-lg font-medium mb-4">How to Use the Coordinator Bot</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>1. Search for the coordinator bot on Telegram</p>
              <p>2. Start a conversation with the bot</p>
              <p>3. Share your phone number using the "Share Contact" button</p>
              <p>4. Your phone number will be verified against coordinator accounts</p>
              <p>5. Once verified, you can manage farmers and view reports</p>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Webhook management is available in the Webhooks tab for administrators.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Coordinator Bot Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Users className="h-8 w-8 mx-auto text-blue-600 mb-2" />
              <div className="text-2xl font-bold">{stats.totalConnections}</div>
              <div className="text-sm text-gray-600">Total Connections</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
              <div className="text-2xl font-bold">{stats.verifiedConnections}</div>
              <div className="text-sm text-gray-600">Verified</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <Clock className="h-8 w-8 mx-auto text-yellow-600 mb-2" />
              <div className="text-2xl font-bold">{stats.pendingConnections}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connections List */}
      <Card>
        <CardHeader>
          <CardTitle>Connected Coordinators</CardTitle>
          <CardDescription>
            Coordinators who have connected to the bot
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No coordinators have connected to the bot yet.
            </div>
          ) : (
            <div className="space-y-4">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <UserCheck className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">
                        {connection.user_profiles?.first_name} {connection.user_profiles?.last_name}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {connection.phone_number}
                        </span>
                        {connection.telegram_username && (
                          <span>@{connection.telegram_username}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Connected: {new Date(connection.verified_at || connection.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={connection.verification_status === 'verified' ? 'default' : 'secondary'}
                    >
                      {connection.verification_status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDisconnectUser(connection.user_id)}
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoordinatorBotManager;